// hooks/useSelectionManager.js - COMPLETE POSITION-BASED VERSION (FIXED)
import { useState, useEffect, useRef, useCallback } from "react";
import { SentenceExtractor } from '../utils/sentenceExtractor';

export default function useSelectionManager(containerRef, zoom, documentKey, highlights = []) { 
  const [selectedText, setSelectedText] = useState("");
  const [selectionRects, setSelectionRects] = useState([]);
  const [selectionContext, setSelectionContext] = useState({ 
    selectedText: "", 
    contextBefore: "", 
    contextAfter: "" 
  });
  const [fullPageText, setFullPageText] = useState("");
  const [hasHighlightOnSelection, setHasHighlightOnSelection] = useState(false);
  const [matchingHighlightIds, setMatchingHighlightIds] = useState([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  
  // Refs to track previous values and debounce
  const prevSelectedTextRef = useRef("");
  const selectionTimeoutRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Helper function to calculate overlap area
  const calculateOverlapArea = (rect1, rect2) => {
    const xOverlap = Math.max(0, 
      Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left)
    );
    const yOverlap = Math.max(0, 
      Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top)
    );
    return xOverlap * yOverlap;
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);

  // Reset on document change
  useEffect(() => {
    setSelectedText("");
    setSelectionRects([]);
    setSelectionContext({ selectedText: "", contextBefore: "", contextAfter: "" });
    setFullPageText("");
    setHasHighlightOnSelection(false);
    setMatchingHighlightIds([]);
    setCurrentPageNumber(1);
    prevSelectedTextRef.current = "";
    
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
      selectionTimeoutRef.current = null;
    }
    
    isProcessingRef.current = false;
  }, [documentKey]);

  // Replace the entire findMatchingHighlights function with this stricter version:
const findMatchingHighlights = useCallback((selectionRects, pageNumber) => {
  console.log('🔍 Finding ALL highlights at position:', {
    selectionRectsCount: selectionRects?.length,
    pageNumber,
    totalHighlights: highlights.length
  });
  
  if (!selectionRects || selectionRects.length === 0 || !highlights || highlights.length === 0) {
    return { hasHighlight: false, highlightIds: [] };
  }

  // Get the container to find text layer
  const container = containerRef.current;
  if (!container) {
    console.log('❌ No container for highlight check');
    return { hasHighlight: false, highlightIds: [] };
  }

  // Find text layer for the current page
  const textLayer = container.querySelector(
    `.react-pdf__Page[data-page-number="${pageNumber}"] .react-pdf__Page__textContent`
  );
  
  if (!textLayer) {
    console.log(`❌ No text layer found for page ${pageNumber}`);
    return { hasHighlight: false, highlightIds: [] };
  }

  const layerRect = textLayer.getBoundingClientRect();
  
  // Get highlights on current page
  const pageHighlights = highlights.filter(h => h.pageNumber === pageNumber);
  console.log(`📄 Found ${pageHighlights.length} highlights on page ${pageNumber}`);

  if (pageHighlights.length === 0) {
    return { hasHighlight: false, highlightIds: [] };
  }

  // Find highlights that ACTUALLY overlap with the selection
  const overlappingHighlightIds = [];
  
  // Calculate the total area of selection
  let totalSelectionArea = 0;
  const normalizedSelectionRects = [];
  
  selectionRects.forEach((selectionRect, rectIndex) => {
    // Convert selection rect to normalized coordinates
    const normalizedSelection = {
      left: (selectionRect.left - layerRect.left) / layerRect.width,
      top: (selectionRect.top - layerRect.top) / layerRect.height,
      width: selectionRect.width / layerRect.width,
      height: selectionRect.height / layerRect.height,
      right: 0,
      bottom: 0
    };
    
    normalizedSelection.right = normalizedSelection.left + normalizedSelection.width;
    normalizedSelection.bottom = normalizedSelection.top + normalizedSelection.height;
    
    const selectionArea = normalizedSelection.width * normalizedSelection.height;
    totalSelectionArea += selectionArea;
    normalizedSelectionRects.push(normalizedSelection);
    
    console.log(`📏 Selection rect ${rectIndex} normalized:`, {
      ...normalizedSelection,
      area: selectionArea
    });
  });

  // Check each highlight against ALL selection rectangles
  pageHighlights.forEach(highlight => {
    // Skip if already in list
    if (overlappingHighlightIds.includes(highlight.id)) return;
    
    // Check if highlight has normalized coordinates
    if (highlight.normalizedLeft === undefined || highlight.normalizedTop === undefined) {
      console.log(`⚠️ Highlight ${highlight.id} missing normalized coordinates`);
      return;
    }

    const highlightBounds = {
      left: highlight.normalizedLeft,
      top: highlight.normalizedTop,
      right: highlight.normalizedLeft + highlight.normalizedWidth,
      bottom: highlight.normalizedTop + highlight.normalizedHeight,
      width: highlight.normalizedWidth,
      height: highlight.normalizedHeight
    };

    const highlightArea = highlightBounds.width * highlightBounds.height;
    console.log(`🔍 Checking highlight ${highlight.id}:`, {
      bounds: highlightBounds,
      area: highlightArea
    });

    // Calculate overlap with ALL selection rectangles
    let totalOverlapArea = 0;
    
    normalizedSelectionRects.forEach((selectionRect, rectIndex) => {
      // Calculate intersection
      const xOverlap = Math.max(0, 
        Math.min(selectionRect.right, highlightBounds.right) - 
        Math.max(selectionRect.left, highlightBounds.left)
      );
      const yOverlap = Math.max(0, 
        Math.min(selectionRect.bottom, highlightBounds.bottom) - 
        Math.max(selectionRect.top, highlightBounds.top)
      );
      
      const overlapArea = xOverlap * yOverlap;
      totalOverlapArea += overlapArea;
      
      if (overlapArea > 0) {
        console.log(`   Rect ${rectIndex} overlap: ${overlapArea.toFixed(6)}`);
      }
    });

    // Calculate what percentage of the SELECTION is covered by this highlight
    const selectionCoverage = totalSelectionArea > 0 ? totalOverlapArea / totalSelectionArea : 0;
    
    // Calculate what percentage of the HIGHLIGHT is covered by selection
    const highlightCoverage = highlightArea > 0 ? totalOverlapArea / highlightArea : 0;
    
    console.log(`📊 Highlight ${highlight.id} coverage:`, {
      totalOverlapArea: totalOverlapArea.toFixed(6),
      totalSelectionArea: totalSelectionArea.toFixed(6),
      selectionCoverage: (selectionCoverage * 100).toFixed(1) + '%',
      highlightCoverage: (highlightCoverage * 100).toFixed(1) + '%'
    });

    // STRICT CRITERIA: The highlight must cover at least 50% of the selection
    // OR the selection must cover at least 50% of the highlight
    if (selectionCoverage >= 0.5 || highlightCoverage >= 0.5) {
      console.log(`✅ Highlight ${highlight.id} SIGNIFICANTLY overlaps with selection!`);
      overlappingHighlightIds.push(highlight.id);
    } else if (totalOverlapArea > 0) {
      console.log(`⚠️ Highlight ${highlight.id} has minor overlap (${(selectionCoverage * 100).toFixed(1)}%), ignoring`);
    }
  });

  console.log('🎯 Found significantly overlapping highlights:', {
    count: overlappingHighlightIds.length,
    ids: overlappingHighlightIds
  });

  return { 
    hasHighlight: overlappingHighlightIds.length > 0, 
    highlightIds: overlappingHighlightIds
  };
}, [containerRef, highlights]);

  const handleSelectionChange = useCallback(() => {
    // Prevent multiple simultaneous processing
    if (isProcessingRef.current) {
      return;
    }

    // Debounce selection processing
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    selectionTimeoutRef.current = setTimeout(() => {
      isProcessingRef.current = true;
      
      const sel = window.getSelection();
      
      // Check if selection is actually valid and not just cursor movement
      if (!sel || sel.isCollapsed) {
        // Only clear if we previously had a selection
        if (prevSelectedTextRef.current) {
          setSelectedText("");
          setSelectionRects([]);
          setSelectionContext({ selectedText: "", contextBefore: "", contextAfter: "" });
          setHasHighlightOnSelection(false);
          setMatchingHighlightIds([]);
          prevSelectedTextRef.current = "";
        }
        isProcessingRef.current = false;
        return;
      }

      const container = containerRef.current;
      if (!container) {
        console.log('❌ No container');
        isProcessingRef.current = false;
        return;
      }

      const textLayer = container.querySelector(".react-pdf__Page__textContent");

      if (
        !textLayer ||
        !textLayer.contains(sel.anchorNode) ||
        !textLayer.contains(sel.focusNode)
      ) {
        // Only clear if we previously had a selection in the PDF
        if (prevSelectedTextRef.current) {
          setSelectedText("");
          setSelectionRects([]);
          setSelectionContext({ selectedText: "", contextBefore: "", contextAfter: "" });
          setHasHighlightOnSelection(false);
          setMatchingHighlightIds([]);
          prevSelectedTextRef.current = "";
        }
        isProcessingRef.current = false;
        return;
      }

      const range = sel.getRangeAt(0);
      const selectedText = sel.toString().trim();
      
      // Skip if text is too short (probably accidental selection)
      if (selectedText.length < 1) {
        isProcessingRef.current = false;
        return;
      }
      
      // Skip if same text is already selected
      if (selectedText === prevSelectedTextRef.current) {
        isProcessingRef.current = false;
        return;
      }

      const clientRects = Array.from(range.getClientRects());
      const layerRect = textLayer.getBoundingClientRect();
      
      // Filter out tiny/invalid rects
      const filteredRects = clientRects.filter(
        (r) =>
          r.width > 1 &&
          r.height > 1 &&
          r.width < layerRect.width * 0.9 &&
          r.height < layerRect.height * 0.9
      );

      if (filteredRects.length === 0) {
        console.log('❌ No valid rects after filtering');
        isProcessingRef.current = false;
        return;
      }

      // Get current page number
      const pageElement = textLayer.closest('.react-pdf__Page');
      const pageNumber = pageElement ? 
        parseInt(pageElement.getAttribute('data-page-number') || 
                pageElement.getAttribute('data-page-number-1') || 1) : 1;
      
      setCurrentPageNumber(pageNumber);

      // Get UNScaled coordinates (with scroll offsets)
      const rects = filteredRects.map((r) => ({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
        pageTop: layerRect.top + window.scrollY,
        pageLeft: layerRect.left + window.scrollX,
        zoomLevel: zoom
      }));

      // Extract context around selection
      const fullTextData = SentenceExtractor.getFullPageText(textLayer);
      const selectionPosition = SentenceExtractor.findSelectionPosition(fullTextData, selectedText);
      const context = SentenceExtractor.extractContextAroundSelection(
        selectedText, 
        fullTextData.fullText, 
        selectionPosition
      );

      // Find ALL matching highlights
      const highlightCheck = findMatchingHighlights(rects, pageNumber);

      console.log('✅ Valid selection detected:', {
        length: selectedText.length,
        zoom,
        pageNumber,
        hasHighlight: highlightCheck.hasHighlight,
        highlightIds: highlightCheck.highlightIds,
        highlightCount: highlightCheck.highlightIds.length,
        rectsCount: rects.length
      });

      // Update state
      setSelectedText(selectedText);
      setSelectionRects(rects);
      setSelectionContext(context);
      setFullPageText(fullTextData.fullText);
      setHasHighlightOnSelection(highlightCheck.hasHighlight);
      setMatchingHighlightIds(highlightCheck.highlightIds);
      
      // Update ref
      prevSelectedTextRef.current = selectedText;
      isProcessingRef.current = false;
      
    }, 300);

  }, [containerRef, zoom, findMatchingHighlights]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Add event listener with passive option for better performance
    document.addEventListener("selectionchange", handleSelectionChange, { passive: true });
    
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [containerRef, handleSelectionChange]);

  // Also check for highlights when page changes
  useEffect(() => {
    if (selectionRects && selectionRects.length > 0) {
      console.log('🔄 Page changed, re-checking highlights for page:', currentPageNumber);
      const highlightCheck = findMatchingHighlights(selectionRects, currentPageNumber);
      setHasHighlightOnSelection(highlightCheck.hasHighlight);
      setMatchingHighlightIds(highlightCheck.highlightIds);
    }
  }, [currentPageNumber, selectionRects, findMatchingHighlights]);

  return { 
    selectedText, 
    selectionRects, 
    selectionContext,
    fullPageText,
    hasHighlightOnSelection,
    matchingHighlightIds,
    currentPageNumber
  };
}